/**
 * OrgMaturityMatrix — the 7×6 Organizational FAIR Maturity surface
 * (DC-1613 2d). Spec: docs/design/v3.0-maturity-matrix-ux-spec.md.
 *
 * The grid is a PROJECTION of placements, not a scorecard: the only filled
 * claim is the evidenced floor; reach renders hollow ("behaviours present"),
 * self-claims render as outlined markers (a claim, not a placement), and
 * dimensions without a shipped evidence path render a designed
 * "not yet assessable" band that says why — never a zero, never an empty
 * cell, never a skeleton promise (DC-1517 honest-state doctrine).
 *
 * Data: GET /placement (live placements) + GET /reference (vendored catalog
 * labels — CC BY 4.0; this surface carries the mandatory attribution footer,
 * the obligation the 2a/2b API slice deferred to 2d). POST /runs backs the
 * consultant-facing record-run affordance (§6.4 minimal self-claim capture).
 *
 * Tokens only (lint:cream MIGRATED). Third lens of the consolidated
 * Readiness destination — the org-capability axis beside the asset and
 * execution axes (spec §1).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid3x3 } from 'lucide-react';

import { useApiClient } from '../../hooks/useApiClient';
import { DimensionDetailPanel } from './DimensionDetailPanel';
import {
  MATURITY_LEVEL_NUMBERS,
  type DimensionPlacement,
  type FairMmReference,
  type MaturityDimensionId,
  type PersistedRun,
  type PlacementResponse,
} from './maturityContract';
import '../../styles/org-maturity.css';

type LoadState = 'loading' | 'error' | 'ready';

type SelfClaims = Partial<Record<MaturityDimensionId, number>>;

// ============================================================================
// Cell projection (spec §3)
// ============================================================================

type CellState = 'achieved' | 'floor' | 'present' | 'empty';

function cellState(p: DimensionPlacement, level: number): CellState {
  if (p.floor !== null && level < p.floor) return 'achieved';
  if (p.floor !== null && level === p.floor) return 'floor';
  if (p.criteria.some((c) => c.level === level && c.satisfied)) return 'present';
  return 'empty';
}

const CELL_STATE_WORDS: Record<CellState, string> = {
  achieved: 'achieved',
  floor: 'floor',
  present: 'behaviours present',
  empty: 'not yet',
};

// ============================================================================
// The disclosure read line (spec §3 — the §6.3 three-value read in words)
// ============================================================================

function placementRead(p: DimensionPlacement): string {
  if (p.floor === null) return '';
  if (p.reach !== null && p.reach > p.floor) {
    const n = p.blocking.length;
    return `L${p.floor} floor · L${p.reach} behaviours present, blocked by ${n} unmet ${
      n === 1 ? 'criterion' : 'criteria'
    }`;
  }
  return `L${p.floor} floor · nothing above in reach`;
}

function claimRead(
  p: DimensionPlacement,
  claim: number,
): { text: string; divergent: boolean } {
  if (!p.assessable || p.floor === null) {
    return {
      text: `Self-assessed L${claim} · not yet comparable — dimension not yet assessable`,
      divergent: false,
    };
  }
  if (claim === p.floor) {
    return { text: `Self-assessed L${claim} · evidence agrees`, divergent: false };
  }
  if (claim > p.floor) {
    return {
      text: `Self-assessed L${claim} · evidence currently supports L${p.floor}`,
      divergent: true,
    };
  }
  return {
    text: `Self-assessed L${claim} · evidence supports L${p.floor}`,
    divergent: true,
  };
}

// ============================================================================
// Record-run dialog (spec §5 — §6.4 minimal self-claim capture)
// ============================================================================

function RecordRunDialog({
  reference,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  reference: FairMmReference;
  submitting: boolean;
  error: string | null;
  onSubmit: (claims: SelfClaims) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [claims, setClaims] = useState<Partial<Record<MaturityDimensionId, string>>>({});

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const selected: SelfClaims = {};
    for (const dim of reference.dimensions) {
      const v = claims[dim.id];
      if (v !== undefined && v !== '') selected[dim.id] = Number(v);
    }
    onSubmit(selected);
  };

  return (
    <div className="omm-dialog-backdrop">
      <div className="omm-dialog" role="dialog" aria-modal="true" aria-labelledby="omm-dialog-title">
        <h3 id="omm-dialog-title" className="omm-dialog-title">
          Record assessment run
        </h3>
        <p className="omm-dialog-context">
          Optional: the organization&rsquo;s own workshop self-placement per dimension. Recorded
          self-claims render on the matrix as ◇ and drive the perception-vs-evidence read.
        </p>
        <form onSubmit={submit}>
          {reference.dimensions.map((dim) => (
            <label key={dim.id} className="omm-dialog-row">
              <span className="omm-dialog-dim">{dim.name}</span>
              <select
                value={claims[dim.id] ?? ''}
                onChange={(e) =>
                  setClaims((prev) => ({ ...prev, [dim.id]: e.target.value }))
                }
              >
                <option value="">Not claimed</option>
                {MATURITY_LEVEL_NUMBERS.map((l) => (
                  <option key={l} value={l}>
                    L{l}
                  </option>
                ))}
              </select>
            </label>
          ))}
          {error && (
            <p className="omm-dialog-error" role="alert">
              {error}
            </p>
          )}
          <div className="omm-dialog-actions">
            <button type="button" className="omm-text-btn" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="omm-btn" disabled={submitting}>
              {submitting ? 'Recording…' : 'Record run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Header + footer
// ============================================================================

function MatrixHeader(): React.ReactElement {
  return (
    <header className="omm-header">
      <div className="omm-eyebrow">Organizational capability</div>
      <h2 className="omm-title">FAIR maturity</h2>
      <p className="omm-subtitle">
        Placements are evidence-inferred from your evaluated datasets and cite the
        framework&rsquo;s own criteria. A dimension without sufficient evidence reads &ldquo;not
        yet assessable&rdquo; — never a score.
      </p>
    </header>
  );
}

function AttributionFooter({
  reference,
  rubricVersion,
}: {
  reference: FairMmReference | null;
  rubricVersion: string | null;
}): React.ReactElement {
  return (
    <p className="omm-attribution">
      Framework: Pistoia FAIR Maturity Matrix (CC BY 4.0) — Pistoia-Alliance-Inc/FAIR-Maturity-Matrix
      {reference ? ` · catalog ${reference.catalog_version}` : ''}
      {rubricVersion ? ` · placements ${rubricVersion}` : ''}
    </p>
  );
}

// ============================================================================
// The view
// ============================================================================

export const OrgMaturityMatrix: React.FC = () => {
  const { authFetch } = useApiClient();
  const [state, setState] = useState<LoadState>('loading');
  const [placementData, setPlacementData] = useState<PlacementResponse | null>(null);
  const [reference, setReference] = useState<FairMmReference | null>(null);
  const [expanded, setExpanded] = useState<MaturityDimensionId | null>(null);
  const [selfClaims, setSelfClaims] = useState<SelfClaims>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordedNote, setRecordedNote] = useState(false);

  // Stale-response guard (DC-1593 pattern).
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState('loading');
    try {
      const [placementRes, referenceRes] = await Promise.all([
        authFetch('/api/organizational-maturity/placement'),
        authFetch('/api/organizational-maturity/reference'),
      ]);
      if (requestSeq.current !== seq) return;
      if (!placementRes.ok) throw new Error(`placement request failed (${placementRes.status})`);
      if (!referenceRes.ok) throw new Error(`reference request failed (${referenceRes.status})`);
      const placementBody = (await placementRes.json()) as PlacementResponse;
      const referenceBody = (await referenceRes.json()) as FairMmReference;
      if (requestSeq.current !== seq) return;
      setPlacementData(placementBody);
      setReference(referenceBody);
      setState('ready');
    } catch {
      if (requestSeq.current !== seq) return;
      setState('error');
    }
  }, [authFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const recordRun = useCallback(
    async (claims: SelfClaims) => {
      setRecording(true);
      setRecordError(null);
      try {
        const res = await authFetch('/api/organizational-maturity/runs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            Object.keys(claims).length > 0 ? { self_claimed: claims } : {},
          ),
        });
        if (res.status !== 201) throw new Error(`run request failed (${res.status})`);
        const run = (await res.json()) as PersistedRun;
        const recorded: SelfClaims = {};
        for (const a of run.assessments) {
          if (a.self_claimed_level !== null) recorded[a.dimension_key] = a.self_claimed_level;
        }
        setSelfClaims(recorded);
        setDialogOpen(false);
        setRecordedNote(true);
      } catch {
        setRecordError('Couldn’t record the run. Nothing was saved — retry when ready.');
      } finally {
        setRecording(false);
      }
    },
    [authFetch],
  );

  const placements = useMemo(() => {
    if (!placementData || !reference) return [];
    const byId = new Map(placementData.placements.map((p) => [p.dimension, p]));
    return reference.dimensions
      .map((d) => byId.get(d.id))
      .filter((p): p is DimensionPlacement => p !== undefined);
  }, [placementData, reference]);

  const rubricVersion = useMemo(
    () => placements.find((p) => p.rubric_version !== null)?.rubric_version ?? null,
    [placements],
  );

  const allPending = placements.length > 0 && placements.every((p) => !p.assessable);

  // ------------------------------------------------------------------ states

  if (state === 'loading') {
    return (
      <div className="omm" aria-busy="true">
        <MatrixHeader />
        <div className="omm-skeleton-block" role="status" aria-label="Loading maturity placements">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="omm-skeleton omm-skeleton--row" />
          ))}
        </div>
        <AttributionFooter reference={reference} rubricVersion={null} />
      </div>
    );
  }

  if (state === 'error' || !placementData || !reference) {
    return (
      <div className="omm">
        <MatrixHeader />
        <div className="omm-error" role="alert">
          <span>Couldn’t load maturity placements.</span>
          <button type="button" className="omm-text-btn" onClick={() => void load()}>
            Retry
          </button>
        </div>
        <AttributionFooter reference={reference} rubricVersion={null} />
      </div>
    );
  }

  const dimName = new Map(reference.dimensions.map((d) => [d.id, d.name]));
  const evidence = placementData.bundle.per_dimension_evidence;

  return (
    <div className="omm">
      <div className="omm-header-row">
        <MatrixHeader />
        <button type="button" className="omm-btn omm-btn--outline" onClick={() => setDialogOpen(true)}>
          Record assessment run
        </button>
      </div>

      {recordedNote && (
        <p className="omm-recorded-note" role="status">
          Run recorded — placements and self-claims are persisted for the engagement record.
        </p>
      )}

      {allPending && (
        <div className="omm-coldstart">
          <Grid3x3 size={24} aria-hidden="true" className="omm-coldstart__icon" />
          <p>
            Maturity placement begins with evidence. Run FAIR analyses on your datasets — the
            FAIR-data dimension places itself from those scores.
          </p>
        </div>
      )}

      <div className="omm-grid-scroll">
        <table className="omm-grid">
          <caption className="omm-visually-hidden">
            Organizational FAIR maturity placement, 7 dimensions by 6 levels
          </caption>
          <thead>
            <tr>
              <th scope="col" className="omm-dim-col">
                Dimension
              </th>
              {reference.levels.map((l) => (
                <th key={l.level} scope="col" className="omm-level-col" title={l.name}>
                  <span className="omm-level-num">L{l.level}</span>
                  <span className="omm-level-metaphor">{l.marketplace_metaphor}</span>
                </th>
              ))}
            </tr>
          </thead>
          {placements.map((p) => {
            const claim = selfClaims[p.dimension] ?? null;
            const isExpanded = expanded === p.dimension;
            const read = p.assessable ? placementRead(p) : null;
            const claimLine = claim !== null ? claimRead(p, claim) : null;
            return (
              <tbody key={p.dimension} className="omm-dim">
                <tr>
                  <th scope="row" className="omm-dim-cell">
                    <button
                      type="button"
                      className="omm-dim-btn"
                      aria-expanded={isExpanded}
                      onClick={() => setExpanded(isExpanded ? null : p.dimension)}
                    >
                      <span className="omm-dim-name">{dimName.get(p.dimension) ?? p.dimension}</span>
                      <span className="omm-dim-confidence">
                        {p.assessable ? p.confidence : 'pending'}
                      </span>
                    </button>
                  </th>
                  {p.assessable ? (
                    reference.levels.map((l) => {
                      const cs = cellState(p, l.level);
                      const isReach =
                        p.reach !== null && p.floor !== null && p.reach > p.floor && l.level === p.reach;
                      return (
                        <td key={l.level} className={`omm-cell omm-cell--${cs}`}>
                          {cs === 'floor' && (
                            <span className="omm-cell-nickname">{l.nickname ?? l.name}</span>
                          )}
                          {isReach && <span className="omm-cell-present">present</span>}
                          <span className="omm-visually-hidden">{CELL_STATE_WORDS[cs]}</span>
                          {claim === l.level && (
                            <span
                              className="omm-claim"
                              aria-label={`self-assessed level ${l.level}`}
                            >
                              ◇
                            </span>
                          )}
                        </td>
                      );
                    })
                  ) : (
                    <td className="omm-pending-band" colSpan={6}>
                      <span className="omm-pending-label">Not yet assessable</span>
                      <span className="omm-pending-reason"> — {p.evidence_summary.replace(/^Not yet assessable: /, '')}</span>
                      {claim !== null && (
                        <span className="omm-claim" aria-label={`self-assessed level ${claim}`}>
                          {' '}
                          ◇ L{claim}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
                {(read || claimLine) && (
                  <tr className="omm-read-row">
                    <td colSpan={7}>
                      {read && <span className="omm-read">{read}</span>}
                      {read && claimLine && <span className="omm-read-sep"> · </span>}
                      {claimLine && (
                        <span
                          className={
                            claimLine.divergent ? 'omm-read omm-read--divergent' : 'omm-read'
                          }
                        >
                          {claimLine.text}
                        </span>
                      )}
                    </td>
                  </tr>
                )}
                {isExpanded && (
                  <tr className="omm-detail-row">
                    <td colSpan={7}>
                      <DimensionDetailPanel
                        placement={p}
                        evidence={evidence[p.dimension]}
                        selfClaim={claim}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </div>

      <AttributionFooter reference={reference} rubricVersion={rubricVersion} />

      {dialogOpen && (
        <RecordRunDialog
          reference={reference}
          submitting={recording}
          error={recordError}
          onSubmit={(claims) => void recordRun(claims)}
          onCancel={() => {
            setDialogOpen(false);
            setRecordError(null);
          }}
        />
      )}
    </div>
  );
};

export default OrgMaturityMatrix;
